import { memo, ReactElement, useCallback, useMemo } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableColumns, ValueLists } from '@ohpen/enso-ui';
import { AssistantPermissionType, FieldType } from '@pmp/api';

import { AdviserListProps, CollaborationRow } from '../interfaces';
import styles from '../styles.module.css';

const DEFAULT_COLLABORATION = AssistantPermissionType.VIEW;

export const AdvisersList = memo(
  ({ readonly, availableAdvisers, currentIntermediaryId, collaborationRows, onCollaborationRowsChange, roleOptions, collaborationOptions }: AdviserListProps): ReactElement => {
    const { t } = useTranslation();

    const valueLists: ValueLists = useMemo(
      () => ({ [FieldType.USER_ROLE_TYPE]: Object.fromEntries((roleOptions ?? []).map(o => [o.value, o.label])) }),
      [roleOptions],
    );

    const advisersById = useMemo(
      () => new Map((availableAdvisers ?? []).map(a => [a.id, a])),
      [availableAdvisers],
    );

    const dataSource = useMemo(
      () =>
        (collaborationRows ?? []).map(row => {
          const adviser = advisersById.get(row.adviserId);
          return {
            ...row,
            name: row.name || adviser?.displayName || row.adviserId,
            roles: row.roles ?? adviser?.roles,
            email: row.email ?? adviser?.email,
          };
        }),
      [collaborationRows, advisersById],
    );

    const handleSelectAdvisers = useCallback(
      (ids: string[]) => {
        const adviserMap = new Map((availableAdvisers ?? []).map(a => [a.id, a]));
        const kept = (collaborationRows ?? []).filter(r => ids.includes(r.adviserId));
        const existingIds = new Set((collaborationRows ?? []).map(r => r.adviserId));
        const added = ids
          .filter(id => !existingIds.has(id))
          .map(id => {
            const adviser = adviserMap.get(id);
            return {
              adviserId: id,
              name: adviser?.displayName ?? id,
              collaborationType: DEFAULT_COLLABORATION,
              roles: adviser?.roles,
              email: adviser?.email,
            };
          });
        onCollaborationRowsChange?.([...kept, ...added]);
      },
      [availableAdvisers, collaborationRows, onCollaborationRowsChange],
    );

    const handleCollaborationChange = useCallback(
      (adviserId: string, value: string) => {
        onCollaborationRowsChange?.(
          (collaborationRows ?? []).map(r => (r.adviserId === adviserId ? { ...r, collaborationType: value } : r)),
        );
      },
      [collaborationRows, onCollaborationRowsChange],
    );

    const handleRemoveRow = useCallback(
      (adviserId: string) => {
        onCollaborationRowsChange?.((collaborationRows ?? []).filter(r => r.adviserId !== adviserId));
      },
      [collaborationRows, onCollaborationRowsChange],
    );

    const columns: TableColumns<CollaborationRow> = useMemo(
      () => [
        {
          title: 'lender.brokers.labels.name',
          dataIndex: 'name',
          key: 'name',
        },
        {
          title: 'lender.brokers.labels.collaboration',
          key: 'collaborationType',
          render: (_: unknown, record: CollaborationRow) => (
            <Select
              value={record.collaborationType}
              options={collaborationOptions}
              onChange={value => handleCollaborationChange(record.adviserId, value)}
              className={styles.collaborationSelect}
              disabled={readonly}
            />
          ),
        },
        {
          title: 'lender.brokers.labels.role',
          dataIndex: 'roles',
          key: 'roles',
          options: {
            format: { type: 'multiValueList' },
            listName: FieldType.USER_ROLE_TYPE,
          },
        },
        {
          title: 'lender.brokers.labels.email',
          dataIndex: 'email',
          key: 'email',
        },
        { title: 'enso.labels.actions', dataIndex: '', key: 'actions', width: 80, fixed: 'right' },
      ],
      [collaborationOptions, handleCollaborationChange, readonly],
    );

    const getRowActions = useCallback(
      (record: CollaborationRow): (ReactElement | null)[] => [
        <Button
          key="remove"
          type="text"
          danger
          aria-label="Remove adviser"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRow(record.adviserId)}
          disabled={readonly}
        />,
      ],
      [handleRemoveRow, readonly],
    );

    const adviserOptions = useMemo(
      () =>
        (availableAdvisers ?? [])
          .filter(a => a.id !== currentIntermediaryId)
          .map(a => ({ label: a.displayName, value: a.id })),
      [availableAdvisers, currentIntermediaryId],
    );

    const selectedIds = (collaborationRows ?? []).map(r => r.adviserId);
    return (
      <Flex vertical gap={16}>
        <Select
          mode="multiple"
          placeholder={t('lender.brokers.collaborations.selectAdvisers')}
          value={selectedIds}
          onChange={handleSelectAdvisers}
          options={adviserOptions}
          disabled={readonly}
        />
        <Table
          id="advisers-collaborations"
          dataSource={dataSource}
          columns={columns}
          rowKey="adviserId"
          valueLists={valueLists}
          getRowActions={getRowActions}
          pagination={false}
          manageColumns={false}
          showViews={false}
          fillHeight={false}
          locale={{ emptyText: t('lender.brokers.collaborations.noAdvisers') }}
        />
      </Flex>
    );
  },
);
